import { FC, useEffect, useRef, useState } from 'react';
import { useAuth } from '@context/AuthContext';
import { MessageContent } from './MessageContent';
import { PaulinaContextValue } from './PaulinaProvider';
import { PaulinaNudgeCard } from './PaulinaNudgeCard';
import { PaulinaNudgeBanner } from './PaulinaNudgeBanner';
import { SUGGESTED_PROMPTS } from './suggestedPrompts';
import { paulinaTrack } from './paulinaTrack';
import { esTipDescubrimiento, type Nudge } from './usePaulinaChat';

/**
 * Cuerpo del chat (lista de mensajes + input) compartido por el panel desktop
 * (PaulinaPanel) y el bottom-sheet móvil (PaulinaSheet). Es presentacional: recibe
 * el estado del chat por props desde el contexto para no duplicar el render de
 * mensajes/input en cada superficie.
 *
 * Empty-state (FIX B): en vez de un texto plano, muestra un saludo cálido (con el
 * nombre del comercio/usuario si está disponible) + "Te puedo ayudar con:" + CHIPS
 * accionables del catálogo curado (suggestedPrompts). Click en un chip ENVÍA el prompt
 * directamente (doSend) y emite telemetría pau_chip_sugerido_click. Al mostrarse los
 * chips emite pau_chips_mostrados (una vez por montaje, para medir impresión → CTR).
 *
 * Calidad: debajo de cada respuesta del ASISTENTE hay 👍/👎 (señal explícita). Una sola
 * votación por mensaje (mapa ID ESTABLE de la respuesta → util) → emite pau_respuesta_util
 * con { util: boolean }. Sin backend, solo telemetría.
 *
 * Persistencia del voto: el voto pertenece a ESA respuesta (su identidad), no a su posición.
 * Cada turno del asistente nace con un id estable (usePaulinaChat) y el voto se keya por ese id,
 * no por índice — así no "salta" de mensaje al recortar el historial ni se hereda en una
 * conversación nueva (que arranca en idx 0,1,2…). Se guarda en sessionStorage con una llave
 * HERMANA del historial (namespace comercio+usuario + sesionChatId de la conversación en curso,
 * prefijo `agent_chat_votes`): sobrevive un reload de la MISMA conversación, una conversación
 * NUEVA nace limpia, y respeta el aislamiento por sesión/sucursal (no arrastra votos entre
 * comercios; logout los barre). Se restaura al montar y al cambiar de conversación/identidad.
 */

type ConversationProps = Pick<
  PaulinaContextValue,
  'turns' | 'input' | 'setInput' | 'busy' | 'error' | 'bottomRef' | 'goTo' | 'send' | 'doSend' | 'sesionChatId'
> & {
  // Nudge completo a renderizar arriba de la conversación (solo MÓVIL: lo pasa PaulinaSheet
  // cuando hay un nudge pendiente). Desktop no los pasa → la tarjeta no se monta y el panel
  // queda intacto (su proactividad sigue siendo el PaulinaNudgeBubble flotante).
  nudge?: Nudge | null;
  aceptarNudge?: (n: Nudge) => void;
  descartarNudge?: (n: Nudge) => void;
};

// Color de marca de Paulina (mismo acento que los botones de navegación del chat).
const PAULINA_BRAND = '#4298B5';
// Tono más oscuro del color de marca para el hover de los chips sólidos.
const PAULINA_BRAND_DARK = '#357a93';

// Lectura defensiva de localStorage: en navegación privada o iframes sandboxed el acceso a
// localStorage puede lanzar SecurityError. Como estas lecturas ocurren en el render (votesKey,
// sesionId), una excepción no atrapada tumbaría el árbol de React. Devolvemos '' ante fallo,
// consistente con el resto del módulo que protege el storage con try/catch.
const safeLocal = (key: string): string => {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
};

// Llave de persistencia de votos: HERMANA de `storageKey()` del historial (usePaulinaChat),
// mismo namespace (comercio, usuario) MÁS el id de la conversación en curso (sesionChatId), para
// que una conversación NUEVA nazca limpia (no herede los votos de la anterior) y un reload de la
// MISMA conversación los conserve. El aislamiento por sesión/sucursal sigue siendo idéntico — un
// login distinto en la misma pestaña jamás lee los votos del negocio anterior. Conserva el prefijo
// 'agent_chat_votes' para que el barrido de logout (startsWith) lo siga matcheando.
const votesKey = (sesionChatId: string): string => {
  const c = safeLocal('id_commerce') || 'anon';
  const u = safeLocal('id_user') || 'anon';
  return `agent_chat_votes:${c}:${u}:${sesionChatId}`;
};

// El voto se keya por el ID ESTABLE de la respuesta del asistente (no por índice de turno): así
// pertenece a ESA respuesta y no "salta" de mensaje al recortar el historial ni se hereda en una
// conversación nueva (que arranca en idx 0,1,2…).
const loadVotos = (sesionChatId: string): Record<string, boolean> => {
  try {
    const raw = sessionStorage.getItem(votesKey(sesionChatId));
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
};

const saveVotos = (sesionChatId: string, votos: Record<string, boolean>) => {
  try {
    sessionStorage.setItem(votesKey(sesionChatId), JSON.stringify(votos));
  } catch {
    /* sin storage: el voto sigue funcionando en memoria, solo no persiste */
  }
};

export const PaulinaConversation: FC<ConversationProps> = ({
  turns,
  input,
  setInput,
  busy,
  error,
  bottomRef,
  goTo,
  send,
  doSend,
  sesionChatId,
  nudge,
  aceptarNudge,
  descartarNudge,
}) => {
  // Nombre para personalizar el saludo: preferimos el del comercio; si no, el del usuario.
  // Ambos vienen del contexto de auth (puede no haber ninguno → saludo genérico).
  const { commerceName, user } = useAuth();
  const nombre = commerceName?.trim() || user?.name?.trim() || '';

  // Voto de calidad por respuesta: ID ESTABLE del turno del asistente → util (true/false). Solo una
  // votación por mensaje; tras votar se resalta el pulgar elegido y se deshabilitan ambos. Se hidrata
  // desde sessionStorage para que sobreviva un reload de la MISMA conversación igual que el historial.
  const [votos, setVotos] = useState<Record<string, boolean>>(() => loadVotos(sesionChatId));

  // Recarga los votos cuando cambia la conversación: al cambiar de identidad (logout→login o cambio
  // de sucursal vía refreshLogin) o al iniciar una conversación nueva (clearConversation regenera el
  // sesionChatId), recargamos los votos namespaceados de la conversación ACTUAL → una conversación
  // nueva nace limpia y no arrastra votos de la anterior. El historial (turns) lo recarga el hook.
  useEffect(() => {
    setVotos(loadVotos(sesionChatId));
  }, [sesionChatId]);

  const enviarChip = (label: string, prompt: string) => {
    if (busy) return;
    paulinaTrack('pau_chip_sugerido_click', { chip: label });
    doSend(prompt);
  };

  const votar = (turnId: string, util: boolean) => {
    if (turnId in votos) return; // una sola votación por mensaje, no re-emite
    setVotos((prev) => {
      const next = { ...prev, [turnId]: util };
      saveVotos(sesionChatId, next); // persiste para que tras recargar aparezca votado y deshabilitado
      return next;
    });
    paulinaTrack('pau_respuesta_util', { util });
  };

  // Impresión de chips: cuando se muestra el empty-state con chips, emite UNA vez
  // pau_chips_mostrados (guard con ref para no re-emitir en cada render del montaje).
  const chipsTracked = useRef(false);
  const hayChips = turns.length === 0;
  useEffect(() => {
    if (hayChips && !chipsTracked.current) {
      chipsTracked.current = true;
      paulinaTrack('pau_chips_mostrados', { count: SUGGESTED_PROMPTS.length });
    }
  }, [hayChips]);

  // ¿El contenedor cablea nudges en esta superficie? (lo hace el sheet móvil; el panel desktop no
  // los pasa porque su proactividad es el PaulinaNudgeBubble flotante). Solo entonces montamos
  // tarjeta/banner aquí.
  const nudgesCableados = Boolean(nudge && aceptarNudge && descartarNudge);
  // El nudge SOLO se muestra como banner compacto si es RELEVANTE (no un tip de descubrimiento):
  // los tips siguen suprimidos con historial por consistencia con la capa proactiva. El hook ya los
  // filtra cuando hay conversación, pero guardamos también aquí para que la regla sea explícita.
  const nudgeBanner = nudgesCableados && nudge && !esTipDescubrimiento(nudge) ? nudge : null;

  return (
    <>
      {/* Banner COMPACTO descartable, FIJO arriba (fuera del scroll): la sugerencia RELEVANTE es
          SIEMPRE entregable aunque haya conversación, sin empujar los mensajes ni hacerse pasar por
          uno. En estado vacío preferimos la tarjeta/saludo completo (abajo); con historial, el
          banner. Reusa los handlers del hook → su CTA funciona idéntico al de la tarjeta/bubble. */}
      {turns.length > 0 && nudgeBanner && aceptarNudge && descartarNudge && (
        <PaulinaNudgeBanner nudge={nudgeBanner} onAccept={aceptarNudge} onDismiss={descartarNudge} />
      )}
      <div className="flex-1 space-y-2 overflow-y-auto p-3 text-sm">
        {/* Nudge completo: tarjeta arriba de la conversación con título + mensaje + CTA. Reusa los
            handlers del hook (aceptarNudge/descartarNudge). El CTA funciona idéntico al del bubble de
            desktop. Solo se monta si el contenedor pasó la data (sheet móvil) y SOLO en estado vacío
            (turns.length === 0): con conversación previa la sugerencia relevante se muestra como
            banner compacto pinned arriba (ver arriba), no como tarjeta que empuje los mensajes. */}
        {turns.length === 0 && nudgesCableados && nudge && aceptarNudge && descartarNudge && (
          <PaulinaNudgeCard nudge={nudge} onAccept={aceptarNudge} onDismiss={descartarNudge} />
        )}
        {turns.length === 0 && (
          <div className="mt-2 px-1">
            <p className="text-[#253746]">
              ¡Hola{nombre ? ` ${nombre}` : ''}! Soy <span className="font-semibold">Paulina</span>, tu asistente de
              Plick.
            </p>
            <p className="mt-2 text-[#64748b]">Te puedo ayudar con:</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {SUGGESTED_PROMPTS.map(({ label, prompt }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => enviarChip(label, prompt)}
                  disabled={busy}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold text-[#ffffff] shadow-sm transition-colors disabled:opacity-50"
                  style={{ borderColor: PAULINA_BRAND, backgroundColor: PAULINA_BRAND }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = PAULINA_BRAND_DARK)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = PAULINA_BRAND)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {turns.map((t, i) => {
          const esAsistente = t.role !== 'user';
          // Voto por IDENTIDAD de la respuesta (su id estable), no por posición. Turnos legacy
          // persistidos antes de este cambio no tienen id → fallback por índice (el comportamiento
          // viejo solo para ellos; las respuestas nuevas siempre traen id y no sufren el salto).
          const turnId = t.id ?? `legacy-${i}`;
          const voto = votos[turnId];
          const yaVoto = turnId in votos;
          return (
            <div key={i}>
              <div
                className={
                  t.role === 'user'
                    ? 'ml-8 rounded-lg rounded-br-none bg-[#253746] px-3 py-2 text-[#ffffff]'
                    : 'mr-8 rounded-lg rounded-bl-none bg-[#f1f4f6] px-3 py-2 text-[#253746]'
                }
              >
                <MessageContent text={t.content} onNavigate={goTo} />
              </div>
              {esAsistente && (
                <div className="mr-8 mt-2 flex items-center gap-2 border-t border-[#e2e8f0] pl-1 pt-1.5">
                  <span className="text-xs text-[#94a3b8]">¿Te sirvió?</span>
                  <button
                    type="button"
                    aria-label="Sí, me sirvió"
                    aria-pressed={voto === true}
                    onClick={() => votar(turnId, true)}
                    disabled={yaVoto}
                    className={`rounded border px-2 py-1 text-xs leading-none transition-colors disabled:cursor-default ${
                      voto === true
                        ? 'border-[#1FC49A] bg-[#1FC49A1f] text-[#0f9a78]'
                        : yaVoto
                          ? 'border-[#e2e8f0] text-[#94a3b8] opacity-50'
                          : 'border-[#e2e8f0] text-[#64748b] hover:bg-[#f1f4f6]'
                    }`}
                  >
                    👍
                  </button>
                  <button
                    type="button"
                    aria-label="No me sirvió"
                    aria-pressed={voto === false}
                    onClick={() => votar(turnId, false)}
                    disabled={yaVoto}
                    className={`rounded border px-2 py-1 text-xs leading-none transition-colors disabled:cursor-default ${
                      voto === false
                        ? 'border-[#dc2626] bg-[#dc26261f] text-[#dc2626]'
                        : yaVoto
                          ? 'border-[#e2e8f0] text-[#94a3b8] opacity-50'
                          : 'border-[#e2e8f0] text-[#64748b] hover:bg-[#f1f4f6]'
                    }`}
                  >
                    👎
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {busy && (
          <div className="mr-8 rounded-lg bg-[#f1f4f6] px-3 py-2 text-[#253746]">
            <span className="animate-pulse">Paulina está escribiendo…</span>
          </div>
        )}
        {error && <p className="text-xs text-[#dc2626]">{error}</p>}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="flex gap-2 border-t border-[#e2e8f0] p-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu pregunta…"
          className="flex-1 rounded-lg border border-[#e2e8f0] bg-[#ffffff] px-3 py-2 text-sm text-[#253746] outline-none"
          disabled={busy}
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="rounded-lg bg-[#253746] px-4 py-2 text-sm font-semibold text-[#ffffff] disabled:opacity-50"
        >
          Enviar
        </button>
      </form>
    </>
  );
};
